import { useMemo } from 'react';
import startCase from 'lodash/startCase';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getAttributedOrders, getSelectedLocationId } from '../../../../store/orders/selectors';
import { getSelectedFilterItems } from '../../../../store/common/selectors';
import { useOrdersFilter } from '../../Shared/hooks';
import { TAssignOrder } from '../../../../types';
import { getMembers } from '../../../../store/team/selectors';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';

export function useAttributedOrdersOrdersFilter() {
  const { t } = useTranslation('orders');
  const attributedOrders = useAppSelector(getAttributedOrders);
  const selectedFilterItems = useAppSelector(getSelectedFilterItems);
  const selectedLocationId = useAppSelector(getSelectedLocationId);
  const members = useAppSelector(getMembers);
  const { personnel } = useAppSelector(getAuthenticateUser);

  const { getOrdersByLocations, getOrdersByAttributed } = useOrdersFilter();

  const getOrdersByLocationsAndPermission = (orders?: Array<TAssignOrder>) => {
    let filteredOrders = orders || attributedOrders;

    // If personnel is not Admin, filter orders by location
    if (personnel.permissionLevel !== 'Admin') {
      const allowedLocationIds = personnel.locations.map((location) => location.id);

      filteredOrders = attributedOrders.filter((order) => allowedLocationIds.includes(order.locationId));
    }
    return filteredOrders;
  };

  const attributedOrdersFilterItems = useMemo(() => {
    const uniqueAttributedItems = attributedOrders.reduce((acc, order) => {
      // Find the member with a matching shopifyId to the order's userId OR matching commissionTags
      const member = members.find(
        (m) => String(m.shopifyId).toLowerCase() === String(order.userId).toLowerCase() // Case-insensitive shopifyId match
          || (Array.isArray(m.commissionTags)
            && Array.isArray(order.tags)
            && order.tags.some((tag) => m.commissionTags?.some((commissionTag) => commissionTag.toLowerCase() === tag.toLowerCase()))
          ),
      );

      // Determine the value and name to associate with this order
      const value = order?.email || String(order?.userId);
      const name = member ? startCase(member.name) || '' : `User ${order.userId}`;

      // Check if the userId or email is already in the accumulator
      const isAttributeExists = acc.find((item) => item.value === value);

      if (!isAttributeExists) {
        acc.push({ value, name });
      }

      return acc;
    }, [] as Array<{ name: string; value: string }>);

    return [
      {
        value: 'attributed',
        selectedKey: 'attributed',
        name: t('assignedTo'),
        children: uniqueAttributedItems,
        isSelected: false,
      },
    ];
  }, [attributedOrders]);

  const getAttributedOrdersByFilters = useMemo(() => {
    let filteredOrders = getOrdersByLocationsAndPermission(attributedOrders);

    if (selectedFilterItems.location?.length) {
      filteredOrders = getOrdersByLocations(attributedOrders) as Array<TAssignOrder>;
    }

    if (selectedFilterItems.attributed?.length) {
      filteredOrders = getOrdersByAttributed(attributedOrders) as Array<TAssignOrder>;
    }

    if (selectedLocationId !== 0) {
      filteredOrders = filteredOrders.filter((order) => order.locationId === selectedLocationId);
    }

    return filteredOrders;
  }, [attributedOrders, selectedFilterItems, selectedLocationId]);

  return {
    attributedOrdersFilterItems,
    getAttributedOrdersByFilters,
    getOrdersByLocationsAndPermission,
  };
}
